import axios from 'axios';
import cookie from 'js-cookie';
import { formatDate } from '../../../utils';

// Get the API base URL from environment variables or use a default
const API_BASE_URL = process.env.REACT_APP_API_URL;

/**
 * Fetch notification count for a specific project
 * @param {string} projectId - The UUID of the project
 * @returns {Promise<number>} - The count of unviewed notifications
 */
export const fetchProjectNotificationCount = async (projectId) => {
    try {
        const response = await axios.get(
            `${API_BASE_URL}/notifications/project/${projectId}/count/unviewed`,
            {
                withCredentials: true,
                headers: {
                    'X-XSRF-TOKEN': cookie.get('XSRF-TOKEN'),
                },
            }
        );
        return response.data.count || response.data || 0;
    } catch (error) {
        console.error(`Error fetching notification count for project ${projectId}:`, error);
        return 0; // Return 0 if there's an error to avoid breaking the UI
    }
};

/**
 * Fetch notification counts for multiple projects
 * @param {Array<string>} projectIds - Array of project UUIDs
 * @returns {Promise<Object>} - Object with projectId as key and count as value
 */
export const fetchMultipleProjectNotificationCounts = async (projectIds) => {
    try {
        // Make parallel requests for all project notification counts
        const countPromises = projectIds.map(async (projectId) => {
            const count = await fetchProjectNotificationCount(projectId);
            return { projectId, count };
        });

        const results = await Promise.allSettled(countPromises);

        // Convert to object format for easy lookup
        const countMap = {};
        results.forEach((result) => {
            if (result.status === 'fulfilled') {
                const { projectId, count } = result.value;
                countMap[projectId] = count;
            }
        });

        return countMap;
    } catch (error) {
        console.error('Error fetching multiple project notification counts:', error);
        return {};
    }
};

/**
 * Fetch all projects for a business
 * @param {string} businessId - The UUID of the business
 * @returns {Promise<Array>} - Array of project objects
 */
export const fetchProjectsForUser = async () => {
    try {
        const response = await axios.get(`${API_BASE_URL}/project/get-projects-for-user`, {
            withCredentials: true,
            headers: {
                'X-XSRF-TOKEN': cookie.get('XSRF-TOKEN'),
            },
        });

        // Extract project IDs for notification count fetching
        const projectIds = response.data.map((project) => project.projectId);

        // Fetch notification counts for all projects in parallel
        const notificationCounts = await fetchMultipleProjectNotificationCounts(projectIds);

        // Transform the data to match our frontend needs
        return response.data.map((project) => ({
            id: project.projectId,
            name: project.name,
            fieldId: project.fieldId,
            businessId: project.businessId,
            address: [project.streetAddress, project.city, project.province]
                .filter(Boolean)
                .join(', '),
            startDate: formatDate(project.creationDate),
            rawStartDate: project.creationDate, // Keep raw date for sorting
            status: project.isActive ? 'Active' : 'Inactive',
            notifications: notificationCounts[project.projectId] || 0,
            pending: project.submittedCount + project.pendingFortisSignoffCount || 0,
            totalDeferredCost: project.totalDeferredCost || 0,
        }));
    } catch (error) {
        console.error('Error fetching projects:', error);
        throw error;
    }
};

/**
 * Create a new project
 * @param {Object} projectData - The project data to create
 * @returns {Promise<Object>} - The created project
 */
export const createProject = async (projectData) => {
    try {
        const response = await axios.post(`${API_BASE_URL}/project/createProject`, projectData, {
            withCredentials: true,
            headers: {
                'X-XSRF-TOKEN': cookie.get('XSRF-TOKEN'),
            },
        });
        return response.data;
    } catch (error) {
        console.error('Error creating project:', error);
        throw error;
    }
};

/**
 * Update an existing project
 * @param {string} projectId - The UUID of the project to update
 * @param {Object} projectData - The updated project data
 * @returns {Promise<Object>} - The updated project
 */
export const updateProject = async (projectId, projectData) => {
    try {
        const response = await axios.put(
            `${API_BASE_URL}/project/update-project/${projectId}`,
            projectData,
            {
                withCredentials: true,
                headers: {
                    'X-XSRF-TOKEN': cookie.get('XSRF-TOKEN'),
                },
            }
        );
        return response.data;
    } catch (error) {
        console.error('Error updating project:', error);
        throw error;
    }
};
